// /js/shared-navbar.js
// Shared MPA session + navbar loader.
// Every page that needs to know who's logged in MUST include this file,
// even pages that have no visible navbar (e.g. reader.html).

import { supabase } from '/js/services/supabaseClient.js';
import { showToast } from '/js/modules/toast.js';

console.log('📌 shared-navbar.js loaded');

// ============================================
// STATE
// ============================================
let currentUser  = null;
let userData     = null;
let isAdmin      = false;
let isSubscriber = false;
let initialized  = false;

// ============================================
// READY PROMISE — resolves once we've fetched session + user row.
// Usage:  await waitForUser();
// ============================================
let userReadyResolve;
let userReadyPromise = new Promise((resolve) => { userReadyResolve = resolve; });
let userReadyDone    = false;

export function waitForUser() {
    if (userReadyDone) return Promise.resolve();
    return userReadyPromise;
}

// ============================================
// PUBLIC GETTER
// ============================================
export function getNavbarUser() {
    return { user: currentUser, data: userData, isAdmin, isSubscriber };
}

// ============================================
// MOUNT GUARD
// ============================================
function getMount() {
    return document.getElementById('navbarLinks');
}

// ============================================
// FETCH SESSION + USER ROW (no DOM work)
// ============================================
async function refreshUserState() {
    const { data: { session } } = await supabase.auth.getSession();
    currentUser = session?.user ?? null;

    if (currentUser) {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('id, email, display_name, role, is_active, subscription, preferences, stats, favorites')
                .eq('id', currentUser.id)
                .single();

            if (!error && data) {
                userData     = data;
                isAdmin      = data.role === 'admin';
                isSubscriber = data.subscription?.isActive === true;
            } else {
                userData = null; isAdmin = false; isSubscriber = false;
            }
        } catch (e) {
            console.warn('[navbar] Could not load user data:', e);
            userData = null; isAdmin = false; isSubscriber = false;
        }
    } else {
        userData = null; isAdmin = false; isSubscriber = false;
    }
}

// ============================================
// RENDER NAVBAR FROM CACHED STATE
// ============================================
function renderNavbarFromState() {
    const navbarLinks = getMount();
    if (!navbarLinks) return;

    const currentPage = getCurrentPage();
    navbarLinks.innerHTML = buildLinks(currentPage);
    wireListeners(navbarLinks);
}

// ============================================
// BUILD LINKS
// ============================================
function buildLinks(currentPage) {
    let links = '';

    links += link('/',        'home',    currentPage, 'fa-home',  'Home');
    links += link('/catalog', 'catalog', currentPage, 'fa-book',  'Books');

    if (currentUser) {
        links += link('/library', 'library', currentPage, 'fa-book-open', 'My Library');

        if (isAdmin) {
            links += `
                <div class="navbar-dropdown">
                    <a href="#" data-dropdown-toggle>
                        <i class="fas fa-cog"></i> Admin
                        <i class="fas fa-chevron-down text-xs ml-1"></i>
                    </a>
                    <div class="navbar-dropdown-menu">
                        <a href="/admin/dashboard"><i class="fas fa-chart-pie"></i> Dashboard</a>
                        <a href="/admin/books"><i class="fas fa-book"></i> Books</a>
                        <a href="/admin/users"><i class="fas fa-users"></i> Users</a>
                        <a href="/admin/reports"><i class="fas fa-chart-bar"></i> Reports</a>
                    </div>
                </div>
            `;
        }

        links += link('/support', 'support', currentPage, 'fa-headset', 'Support');

        const displayName =
            userData?.display_name ||
            currentUser.user_metadata?.display_name ||
            currentUser.email?.split('@')[0] ||
            'Account';

        links += `
            <div class="navbar-dropdown">
                <a href="#" data-dropdown-toggle>
                    <i class="fas fa-user-circle"></i> ${escapeHtml(displayName)}
                    <i class="fas fa-chevron-down text-xs ml-1"></i>
                </a>
                <div class="navbar-dropdown-menu">
                    <a href="/profile"><i class="fas fa-user"></i> Profile</a>
                    <a href="/subscription"><i class="fas fa-crown"></i> Subscription</a>
                    <hr class="my-2 border-gray-200">
                    <a href="#" data-action="logout" style="color:#EF4444;">
                        <i class="fas fa-sign-out-alt"></i> Logout
                    </a>
                </div>
            </div>
        `;
    } else {
        links += link('/login',    'login',    currentPage, 'fa-sign-in-alt', 'Login');
        links += link('/register', 'register', currentPage, 'fa-user-plus',   'Sign Up');
        links += link('/support',  'support',  currentPage, 'fa-headset',     'Support');
    }

    return links;
}

function link(href, key, currentPage, icon, label) {
    const active = currentPage === key ? 'active' : '';
    return `<a href="${href}" class="${active}"><i class="fas ${icon}"></i> ${label}</a>`;
}

// ============================================
// WIRE LISTENERS
// ============================================
function wireListeners(root) {
    root.querySelectorAll('[data-dropdown-toggle]').forEach(toggle => {
        toggle.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const parent = toggle.closest('.navbar-dropdown');
            root.querySelectorAll('.navbar-dropdown.open').forEach(d => {
                if (d !== parent) d.classList.remove('open');
            });
            parent?.classList.toggle('open');
        });
    });

    root.querySelectorAll('[data-action="logout"]').forEach(el => {
        el.addEventListener('click', (e) => {
            e.preventDefault();
            handleLogout();
        });
    });
}

// Outside-click closes any open dropdown (attach once)
document.addEventListener('click', () => {
    document.querySelectorAll('.navbar-dropdown.open')
        .forEach(d => d.classList.remove('open'));
});

// ============================================
// HELPERS
// ============================================
function getCurrentPage() {
    let path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    if (path.endsWith('.html')) path = path.replace(/\.html$/, '');
    if (!path || path === 'index') return 'home';
    return path;
}

function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, ch => ({
        '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[ch]));
}

// ============================================
// LOGOUT
// ============================================
export async function handleLogout() {
    try {
        await supabase.auth.signOut();
        showToast('Logged out successfully', 'success');
        sessionStorage.removeItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectParams');
        window.location.href = '/';
    } catch (error) {
        console.error('Logout error:', error);
        showToast('Error logging out', 'error');
    }
}
window.handleLogout = handleLogout;

// ============================================
// INIT
// ============================================
async function init() {
    if (initialized) return;
    initialized = true;

    // 1. Fetch session + user row (always, even if no navbar on the page)
    try {
        await refreshUserState();
    } finally {
        // Mark ready even if refresh failed — otherwise callers hang forever
        userReadyDone = true;
        userReadyResolve();
    }

    // 2. Render navbar if this page has one
    if (getMount()) {
        renderNavbarFromState();
    } else {
        console.log('[navbar] no #navbarLinks on this page — session loaded, navbar skipped');
    }

    // 3. Notify any listeners (redundant with waitForUser, but kept for old code)
    window.dispatchEvent(new CustomEvent('navbar:rendered'));

    // 4. Listen for auth changes
    let firstEvent = true;
    supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('🔐 Auth changed:', event, session?.user?.email);

        if (firstEvent) {
            firstEvent = false;
            if (event === 'INITIAL_SESSION') return;   // already handled by refreshUserState
        }

        await refreshUserState();
        if (getMount()) renderNavbarFromState();
        window.dispatchEvent(new CustomEvent('navbar:rendered'));
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Manual refresh (used by profile page after name update, subscription page, etc.)
window.refreshNavbar = async () => {
    await refreshUserState();
    if (getMount()) renderNavbarFromState();
    window.dispatchEvent(new CustomEvent('navbar:rendered'));
};